import { FC, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Chip,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import { tokens } from '../../theme';
import { FilterContext } from '../../contexts/FilterContext';
import {
  fetchWebinarRevenueAttribution,
  fetchWebinarOrderMappings,
  fetchWebinarFilterOptions,
  fetchWebinarGroupedEvaluation,
  fetchWebinarAffiliateGroupingOverview,
  fetchWebinarSourceEfficiency,
  SourceEfficiencyData,
  WebinarRevenueAttributionData,
  WebinarOrderMappingData,
  WebinarFilterOptions,
  WebinarGroupedEvaluationData,
  WebinarAffiliateGroupingOverview,
  AD_TYPE_OPTIONS,
  BLOG_OPTIONS,
  DesignAbData,
  fetchWebinarDesignAb,
} from '../../helpers/webinarStatisticsData';
import OrderMappingsTab from './OrderMappingsTab';
import AffiliateGroupingsTab from './AffiliateGroupingsTab';
import GroupedEvaluationsTab from './GroupedEvaluationsTab';
import SourceEfficiencyTab from './SourceEfficiencyTab';
import DesignAbTab from './DesignAbTab';
import RevenueAttributionView from './RevenueAttributionView';
import KpiFormulaSheet from './kpi/KpiFormulaSheet';
import { Refresh } from '@mui/icons-material';
import Header from '../../components/Header';
import moment from 'moment';

/**
 * Hoechster gueltiger Tab-Index — MUSS mitwandern, wenn unten ein <Tab> dazukommt.
 * Der Mount-Parser hatte die Grenze hart auf 3, waehrend die Leiste schon fuenf Tabs rendert und
 * der URL-Sync `?tab=4` schreibt: ein geteilter Quellen-Effizienz-Link oeffnete beim Empfaenger
 * die Umsatz-Attribution, also andere Zahlen als gemeint. Als Konstante, weil ein blankes `<= 4`
 * beim naechsten Tab genauso verrottet.
 */
const LAST_TAB_INDEX = 6;

// Timezone-safe date formatter for URL parameters
const formatDateForURL = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const WebinarAuswertungen: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { filterState, setFilterState } = useContext(FilterContext)!;

  // Product filtering
  const [filterOptions, setFilterOptions] = useState<WebinarFilterOptions>({
    products: [],
  });
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  // Ad-Type and Blog category filters
  const [selectedAdTypes, setSelectedAdTypes] = useState<string[]>([]);
  const [selectedBlogs, setSelectedBlogs] = useState<string[]>([]);

  // Affiliate/Adref filters
  const [affiliateContainsInput, setAffiliateContainsInput] = useState('');
  const [affiliateNotContainsInput, setAffiliateNotContainsInput] = useState('');
  const [adrefContainsInput, setAdrefContainsInput] = useState('');
  const [adrefNotContainsInput, setAdrefNotContainsInput] = useState('');

  // Debounced values (used for API calls)
  const [affiliateContains, setAffiliateContains] = useState('');
  const [affiliateNotContains, setAffiliateNotContains] = useState('');
  const [adrefContains, setAdrefContains] = useState('');
  const [adrefNotContains, setAdrefNotContains] = useState('');

  const [tabValue, setTabValue] = useState(0);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [revenueAttributionData, setRevenueAttributionData] =
    useState<WebinarRevenueAttributionData | null>(null);
  const [orderMappingData, setOrderMappingData] = useState<WebinarOrderMappingData[]>([]);
  const [selectedMappingMethod, setSelectedMappingMethod] = useState<string>('all');

  const [loading, setLoading] = useState(false);

  // Tab-specific data states
  const [groupedData, setGroupedData] = useState<WebinarGroupedEvaluationData | null>(null);
  const [designAbData, setDesignAbData] = useState<DesignAbData | null>(null);
  const [sourceEfficiencyData, setSourceEfficiencyData] = useState<SourceEfficiencyData | null>(
    null,
  );
  // Default bewusst nur FT: nur dort gibt es fuer ALLE Zeilen einen Nenner. Die Optionsliste
  // kommt aus der Antwort (`meta.publisher_options`), damit Backend und UI nicht auseinanderlaufen.
  const [selectedPublishers, setSelectedPublishers] = useState<string[]>(['ft']);
  // Default 'raw': ein Reaktivierungs-Kontakt ist ein Werbekontakt. 'normalized' rechnet nur den
  // aktiven Teil, waehrend der Zaehler alle Anmeldungen enthaelt — die Quote faellt dann zu gut aus.
  const [reachBasis, setReachBasis] = useState<'raw' | 'normalized'>('raw');
  const [affiliateData, setAffiliateData] = useState<WebinarAffiliateGroupingOverview | null>(null);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await fetchWebinarFilterOptions();
        setFilterOptions(options);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Debounce affiliate/adref filter inputs (1.5 second delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setAffiliateContains(affiliateContainsInput);
    }, 1500);
    return () => clearTimeout(timer);
  }, [affiliateContainsInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAffiliateNotContains(affiliateNotContainsInput);
    }, 1500);
    return () => clearTimeout(timer);
  }, [affiliateNotContainsInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAdrefContains(adrefContainsInput);
    }, 1500);
    return () => clearTimeout(timer);
  }, [adrefContainsInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAdrefNotContains(adrefNotContainsInput);
    }, 1500);
    return () => clearTimeout(timer);
  }, [adrefNotContainsInput]);

  // Tab-specific data loading function
  const handleLoadData = async () => {
    setLoading(true);
    try {
      // NICHT toISOString(): der Picker liefert LOKALE Mitternacht, also ist 01.05. um 00:00 CEST
      // in UTC der 30.04. um 22:00 — jede Anfrage lief dadurch ein Fenster zu frueh, waehrend die
      // URL (formatDateForURL) den richtigen Tag zeigte. Gemessen 2026-08-10: Auswahl
      // 01.05.–10.08. ging als startDate=2026-04-30&endDate=2026-08-09 raus. Betrifft alle Tabs.
      const startDate = filterState.startDate ? formatDateForURL(filterState.startDate) : undefined;
      const endDate = filterState.endDate ? formatDateForURL(filterState.endDate) : undefined;

      if (tabValue === 0) {
        // Tab 0: Revenue Attribution
        const [revenueData, mappingData] = await Promise.all([
          fetchWebinarRevenueAttribution(
            startDate,
            endDate,
            selectedProducts.length > 0 ? selectedProducts : undefined,
            affiliateContains || undefined,
            affiliateNotContains || undefined,
            adrefContains || undefined,
            adrefNotContains || undefined,
            selectedAdTypes.length > 0 ? selectedAdTypes : undefined,
            selectedBlogs.length > 0 ? selectedBlogs : undefined,
          ),
          fetchWebinarOrderMappings(startDate, endDate, ['ft'], selectedMappingMethod),
        ]);
        setRevenueAttributionData(revenueData);
        setOrderMappingData(mappingData);
      } else if (tabValue === 1) {
        // Tab 1: Order Mappings
        const mappingData = await fetchWebinarOrderMappings(
          startDate,
          endDate,
          ['ft'],
          selectedMappingMethod,
        );
        setOrderMappingData(mappingData);
      } else if (tabValue === 2) {
        // Tab 2: Affiliate Groupings
        const data = await fetchWebinarAffiliateGroupingOverview();
        setAffiliateData(data);
      } else if (tabValue === 3) {
        // Tab 3: Grouped Evaluations
        const data = await fetchWebinarGroupedEvaluation(
          startDate,
          endDate,
          selectedProducts.length > 0 ? selectedProducts : undefined,
        );
        setGroupedData(data);
      } else if (tabValue === 4) {
        // Tab 4: Quellen-Effizienz (kein Produktfilter — die Reichweite je Ausgabe/Versand
        // liesse sich nicht produktscharf aufteilen)
        const data = await fetchWebinarSourceEfficiency(
          startDate,
          endDate,
          selectedPublishers,
          reachBasis,
        );
        setSourceEfficiencyData(data);
      } else if (tabValue === 5) {
        // Tab 5: Design-A/B — kein Produkt-/Markenfilter, der Test laeuft nur auf FT-Anmeldeseiten.
        const data = await fetchWebinarDesignAb(startDate, endDate);
        setDesignAbData(data);
      }
    } catch (error) {
      console.error('Error loading webinar statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const updates: any = {};

    // Read startDate from URL
    const startDateParam = urlParams.get('startDate');
    if (startDateParam) {
      const parsedDate = moment(startDateParam, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        updates.startDate = parsedDate.toDate();
      }
    }

    // Read endDate from URL
    const endDateParam = urlParams.get('endDate');
    if (endDateParam) {
      const parsedDate = moment(endDateParam, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        updates.endDate = parsedDate.toDate();
      }
    }

    // Read products from URL
    const productsParam = urlParams.get('products');
    if (productsParam) {
      const productArray = productsParam.split(',').filter((p) => p.trim());
      if (productArray.length > 0) {
        setSelectedProducts(productArray);
      }
    }

    // Read affiliate/adref filters from URL
    const affiliateContainsParam = urlParams.get('affiliateContains');
    if (affiliateContainsParam) {
      setAffiliateContainsInput(affiliateContainsParam);
      setAffiliateContains(affiliateContainsParam);
    }

    const affiliateNotContainsParam = urlParams.get('affiliateNotContains');
    if (affiliateNotContainsParam) {
      setAffiliateNotContainsInput(affiliateNotContainsParam);
      setAffiliateNotContains(affiliateNotContainsParam);
    }

    const adrefContainsParam = urlParams.get('adrefContains');
    if (adrefContainsParam) {
      setAdrefContainsInput(adrefContainsParam);
      setAdrefContains(adrefContainsParam);
    }

    const adrefNotContainsParam = urlParams.get('adrefNotContains');
    if (adrefNotContainsParam) {
      setAdrefNotContainsInput(adrefNotContainsParam);
      setAdrefNotContains(adrefNotContainsParam);
    }

    // Read adTypes from URL
    const adTypesParam = urlParams.get('adTypes');
    if (adTypesParam) {
      const adTypesArray = adTypesParam.split(',').filter((t) => AD_TYPE_OPTIONS.includes(t));
      if (adTypesArray.length > 0) {
        setSelectedAdTypes(adTypesArray);
      }
    }

    // Read blogs from URL
    const blogsParam = urlParams.get('blogs');
    if (blogsParam) {
      const blogsArray = blogsParam.split(',').filter((b) => BLOG_OPTIONS.includes(b));
      if (blogsArray.length > 0) {
        setSelectedBlogs(blogsArray);
      }
    }

    // Read tab from URL
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= LAST_TAB_INDEX) {
        setTabValue(tabIndex);
      }
    }

    // Update filter state with URL parameters
    if (updates.startDate || updates.endDate) {
      setFilterState((prev) => ({
        ...prev,
        ...(updates.startDate && { startDate: updates.startDate }),
        ...(updates.endDate && { endDate: updates.endDate }),
      }));
    }

    // Mark initialization as complete and trigger initial data load
    setTimeout(() => {
      setIsInitialMount(false);
    }, 0);
  }, []); // Run only once on mount

  // Sync URL with filter state changes
  useEffect(() => {
    // Skip URL sync on initial mount to prevent overwriting URL parameters
    if (isInitialMount) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    // Update date parameters
    if (filterState.startDate) {
      urlParams.set('startDate', formatDateForURL(filterState.startDate));
    } else {
      urlParams.delete('startDate');
    }

    if (filterState.endDate) {
      urlParams.set('endDate', formatDateForURL(filterState.endDate));
    } else {
      urlParams.delete('endDate');
    }

    // Update products parameter
    if (selectedProducts.length > 0) {
      urlParams.set('products', selectedProducts.join(','));
    } else {
      urlParams.delete('products');
    }

    // Update tab parameter
    if (tabValue > 0) {
      urlParams.set('tab', tabValue.toString());
    } else {
      urlParams.delete('tab');
    }

    // Update affiliate/adref parameters
    if (affiliateContains) {
      urlParams.set('affiliateContains', affiliateContains);
    } else {
      urlParams.delete('affiliateContains');
    }

    if (affiliateNotContains) {
      urlParams.set('affiliateNotContains', affiliateNotContains);
    } else {
      urlParams.delete('affiliateNotContains');
    }

    if (adrefContains) {
      urlParams.set('adrefContains', adrefContains);
    } else {
      urlParams.delete('adrefContains');
    }

    if (adrefNotContains) {
      urlParams.set('adrefNotContains', adrefNotContains);
    } else {
      urlParams.delete('adrefNotContains');
    }

    // Update adTypes parameter
    if (selectedAdTypes.length > 0) {
      urlParams.set('adTypes', selectedAdTypes.join(','));
    } else {
      urlParams.delete('adTypes');
    }

    // Update blogs parameter
    if (selectedBlogs.length > 0) {
      urlParams.set('blogs', selectedBlogs.join(','));
    } else {
      urlParams.delete('blogs');
    }

    // Update URL without adding to browser history
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [
    filterState.startDate,
    filterState.endDate,
    selectedProducts,
    tabValue,
    affiliateContains,
    affiliateNotContains,
    adrefContains,
    adrefNotContains,
    selectedAdTypes,
    selectedBlogs,
    isInitialMount,
  ]);

  // Auto-load data on initial mount (after URL params are parsed)
  useEffect(() => {
    if (isInitialMount || !filterState.startDate || !filterState.endDate) {
      return;
    }
    // Tab 4 traegt keine Filterleiste, also gibt es dort nichts einzustellen, bevor geladen wird —
    // ein Pflicht-Klick auf "Laden" waere reine Reibung. Einmal beim Oeffnen holen; danach haelt der
    // Guard, und fuer einen neuen Zeitraum gibt es den "Laden"-Button wie bei Tab 2/3.
    // Tab 6 (Kennzahlen & Formeln) ist statisch.
    if (tabValue === 6) return;
    if (tabValue === 4) {
      // Auch nachladen, wenn die Markenauswahl von der geladenen abweicht: sonst stehen im
      // Auswahlfeld Marken, die in den Zahlen darunter nicht drin sind — die schlimmere
      // Fehlbedienung, weil sie wie ein Messergebnis aussieht.
      const geladen = sourceEfficiencyData?.meta.publishers ?? [];
      const abweichend =
        geladen.length !== selectedPublishers.length ||
        selectedPublishers.some((p) => !geladen.includes(p)) ||
        sourceEfficiencyData?.meta.reach_basis !== reachBasis;
      if (!sourceEfficiencyData || abweichend) {
        handleLoadData();
      }
      return;
    }
    // Tab 5 traegt wie 2/3 keine eigene Filterleiste — ohne diesen Zweig stuende der Tab beim
    // Wechsel von einem bereits geladenen Tab 0 leer da, weil die Bedingung darunter nur auf
    // revenueAttributionData schaut (Codex-Befund 2026-08-31).
    if (tabValue === 5) {
      if (!designAbData) {
        handleLoadData();
      }
      return;
    }
    if (!revenueAttributionData) {
      handleLoadData();
    }
    // sourceEfficiencyData/revenueAttributionData bewusst NICHT in den Deps: sie sind die
    // Abbruchbedingung, als Dependency wuerde der Effect nach jedem Laden erneut auswerten.
  }, [
    isInitialMount,
    tabValue,
    filterState.startDate,
    filterState.endDate,
    selectedPublishers,
    reachBasis,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenWebinar = (webinarId: string) => {
    navigate(`/webinar-auswertungen/${webinarId}`);
  };

  return (
    <Box m="20px">
      <Header
        title="Webinar Auswertungen"
        subtitle="Umsatz-Attribution und Order-Mapping Analyse"
      />

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* Filter Controls - Only show for Umsatz-Attribution tab */}
          {tabValue === 0 && (
            <Box
              mb={3}
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="product-select-label">Produkt</InputLabel>
                <Select<string[]>
                  labelId="product-select-label"
                  id="product-select"
                  multiple
                  value={selectedProducts}
                  onChange={(event) => {
                    const value =
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;
                    setSelectedProducts(value);
                  }}
                  input={<OutlinedInput id="select-multiple-product-chip" label="Produkt" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => {
                        const artikel = filterOptions.products.find((p) => p.artikel === val);
                        return <Chip key={val} label={artikel?.name || val} size="small" />;
                      })}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 400 },
                    },
                  }}
                >
                  {filterOptions.products.map((product) => (
                    <MenuItem key={product.artikel} value={product.artikel}>
                      {product.name} ({product.artikel})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Ad-Type category filter */}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="adtype-select-label">Ad-Type</InputLabel>
                <Select<string[]>
                  labelId="adtype-select-label"
                  id="adtype-select"
                  multiple
                  value={selectedAdTypes}
                  onChange={(event) => {
                    const value =
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;
                    setSelectedAdTypes(value);
                  }}
                  input={<OutlinedInput id="select-multiple-adtype-chip" label="Ad-Type" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 400 },
                    },
                  }}
                >
                  {AD_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Blog category filter */}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="blog-select-label">Blog</InputLabel>
                <Select<string[]>
                  labelId="blog-select-label"
                  id="blog-select"
                  multiple
                  value={selectedBlogs}
                  onChange={(event) => {
                    const value =
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;
                    setSelectedBlogs(value);
                  }}
                  input={<OutlinedInput id="select-multiple-blog-chip" label="Blog" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 400 },
                    },
                  }}
                >
                  {BLOG_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Affiliate/Adref filters */}
              <TextField
                label="Affiliate enthält"
                value={affiliateContainsInput}
                onChange={(e) => setAffiliateContainsInput(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                placeholder="1,5s Verzögerung"
              />
              <TextField
                label="Affiliate enthält nicht"
                value={affiliateNotContainsInput}
                onChange={(e) => setAffiliateNotContainsInput(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                placeholder="1,5s Verzögerung"
              />
              <TextField
                label="Adref enthält"
                value={adrefContainsInput}
                onChange={(e) => setAdrefContainsInput(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                placeholder="1,5s Verzögerung"
              />
              <TextField
                label="Adref enthält nicht"
                value={adrefNotContainsInput}
                onChange={(e) => setAdrefNotContainsInput(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                placeholder="1,5s Verzögerung"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleLoadData}
                startIcon={<Refresh />}
                sx={{ height: 40, minWidth: 100 }}
              >
                Laden
              </Button>
            </Box>
          )}

          {/* Load button for tabs without filter controls */}
          {(tabValue === 2 || tabValue === 3 || tabValue === 4 || tabValue === 5) && (
            <Box mb={3} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Marken-Auswahl nur auf Tab 4 — die anderen Tabs sind ohnehin markenübergreifend. */}
              {tabValue === 4 && (
                <FormControl sx={{ minWidth: 260 }} size="small">
                  <InputLabel id="publisher-select-label">Marken</InputLabel>
                  <Select<string[]>
                    labelId="publisher-select-label"
                    id="publisher-select"
                    multiple
                    value={selectedPublishers}
                    onChange={(event) => {
                      const value =
                        typeof event.target.value === 'string'
                          ? event.target.value.split(',')
                          : event.target.value;
                      // Leere Auswahl nicht zulassen: der Endpoint fiele auf FT zurück und die
                      // Anzeige widerspräche dem, was im Feld steht.
                      setSelectedPublishers(value.length > 0 ? value : ['ft']);
                    }}
                    input={<OutlinedInput id="select-multiple-publisher" label="Marken" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip
                            key={val}
                            label={
                              sourceEfficiencyData?.meta.publisher_options.find(
                                (o) => o.key === val,
                              )?.label ?? val
                            }
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {(
                      sourceEfficiencyData?.meta.publisher_options ?? [
                        { key: 'ft', label: 'Finanztrends', has_newsletter_reach: true },
                      ]
                    ).map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.label}
                        {!option.has_newsletter_reach && (
                          <Typography
                            variant="caption"
                            sx={{ ml: 1, color: colors.grey[500] }}
                            component="span"
                          >
                            (keine Newsletter-Reichweite)
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* Nur zeigen, wenn im Fenster überhaupt Reaktivierungs-Versände liegen — sonst ist
                  der Schalter folgenlos und stiftet nur Verwirrung. */}
              {tabValue === 4 && (sourceEfficiencyData?.meta.reaktivierungs_sends ?? 0) > 0 && (
                <FormControl sx={{ minWidth: 230 }} size="small">
                  <InputLabel id="reach-basis-label">Werbekontakte</InputLabel>
                  <Select
                    labelId="reach-basis-label"
                    id="reach-basis-select"
                    value={reachBasis}
                    label="Werbekontakte"
                    onChange={(e) => setReachBasis(e.target.value as 'raw' | 'normalized')}
                  >
                    <MenuItem value="raw">Alle angeschriebenen</MenuItem>
                    <MenuItem value="normalized">Nur aktive Empfänger</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleLoadData}
                startIcon={<Refresh />}
                sx={{ height: 40, minWidth: 100 }}
              >
                Laden
              </Button>
            </Box>
          )}

          {/* scrollable: die fuenf Labels brauchen 802 px, auf dem Tablet stehen 701 zur Verfuegung.
              Ohne das schiebt die Leiste die ganze Seite horizontal auf, statt selbst zu scrollen. */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 3, maxWidth: '100%' }}
          >
            <Tab label="Umsatz-Attribution" />
            <Tab label="Webinar User Mappings" />
            <Tab label="Affiliate Gruppierungen" />
            <Tab label="Gruppierte Auswertungen" />
            <Tab label="Quellen-Effizienz" />
            <Tab label="Design-A/B" />
            <Tab label="Kennzahlen & Formeln" />
          </Tabs>

          {/* Tab 0: Revenue Attribution */}
          {tabValue === 0 && revenueAttributionData && (
            <RevenueAttributionView
              data={revenueAttributionData}
              colors={colors}
              onOpenWebinar={handleOpenWebinar}
            />
          )}

          {/* Tab 1: Order Mappings */}
          {tabValue === 1 && (
            <OrderMappingsTab
              data={orderMappingData}
              loading={loading}
              colors={colors}
              onMappingMethodChange={setSelectedMappingMethod}
              selectedMappingMethod={selectedMappingMethod}
            />
          )}

          {/* Tab 2: Affiliate Groupings */}
          {tabValue === 2 && <AffiliateGroupingsTab colors={colors} data={affiliateData} />}

          {/* Tab 3: Grouped Evaluations */}
          {tabValue === 3 && <GroupedEvaluationsTab colors={colors} data={groupedData} />}

          {/* Tab 4: Quellen-Effizienz (FT) — Anfos je 1.000 Werbekontakte */}
          {tabValue === 4 && <SourceEfficiencyTab colors={colors} data={sourceEfficiencyData} />}
          {tabValue === 5 && <DesignAbTab colors={colors} data={designAbData} />}
          {/* Tab 6: statisch, laedt nichts */}
          {tabValue === 6 && <KpiFormulaSheet />}
        </>
      )}
    </Box>
  );
};

export default WebinarAuswertungen;
